import { useState } from "react";
import {
  Box,
  ButtonBase,
  Chip,
  CircularProgress,
  ListItemText,
  Menu,
  MenuItem,
  Typography,
} from "@mui/material";
import { Apartment, UnfoldMore } from "@mui/icons-material";
import { useMySchools } from "@/shared/hooks/useMySchools";
import { useSchoolSwitch } from "@/shared/hooks/useSchoolSwitch";

/**
 * Sidebar'daki hızlı okul değiştirici — yalnız birden çok okulda yetkili kullanıcıda görünür
 * (tek okulluda null döner, sidebar pikseli değişmez). Seçim paneli o okula geçirir.
 */
export const SchoolSwitcher = () => {
  const { data } = useMySchools();
  const { switchTo, switchingId } = useSchoolSwitch();
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);

  if (!data || data.schools.length < 2) return null;
  const current = data.schools.find((s) => s.current);

  return (
    <Box sx={{ px: 1.25, py: 1, borderBottom: 1, borderColor: "divider", flexShrink: 0 }}>
      <ButtonBase
        onClick={(e) => setAnchor(e.currentTarget)}
        sx={{
          width: "100%",
          px: 1.25,
          py: 0.75,
          borderRadius: 2,
          display: "flex",
          alignItems: "center",
          gap: 1,
          textAlign: "left",
          border: 1,
          borderColor: "divider",
          "&:hover": { bgcolor: "action.hover", borderColor: "primary.main" },
        }}
      >
        <Apartment sx={{ fontSize: 18, color: "primary.main", flexShrink: 0 }} />
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography sx={{ fontSize: 10, color: "text.disabled", lineHeight: 1.2 }}>Aktif Okul</Typography>
          <Typography sx={{ fontSize: 12.5, fontWeight: 650, lineHeight: 1.25 }} noWrap>
            {current?.schoolName ?? "Okul seçin"}
          </Typography>
        </Box>
        {switchingId ? (
          <CircularProgress size={14} sx={{ flexShrink: 0 }} />
        ) : (
          <UnfoldMore sx={{ fontSize: 16, color: "text.disabled", flexShrink: 0 }} />
        )}
      </ButtonBase>

      <Menu
        anchorEl={anchor}
        open={!!anchor}
        onClose={() => setAnchor(null)}
        slotProps={{ paper: { sx: { width: 248, maxHeight: 360 } } }}
      >
        {data.schools.map((s) => (
          <MenuItem
            key={s.recordId}
            selected={s.current}
            disabled={!s.active || (!!switchingId && switchingId !== s.recordId)}
            onClick={() => {
              setAnchor(null);
              if (!s.current) void switchTo(s.recordId, s.schoolName);
            }}
          >
            <ListItemText
              primary={s.schoolName}
              secondary={`${s.studentCount.toLocaleString("tr-TR")} öğrenci · ${s.roleLabel}`}
              primaryTypographyProps={{ fontSize: 13.5, noWrap: true }}
              secondaryTypographyProps={{ fontSize: 11 }}
            />
            {!s.active && <Chip label="Pasif" size="small" sx={{ ml: 1 }} />}
          </MenuItem>
        ))}
      </Menu>
    </Box>
  );
};
